<template>
	<div class="header-banner">
		<template v-if="isEnableUpload">
			<template v-if="realRenderBanner">
				<div class="header-banner-mask">
					<div class="header-banner-change" @click="handleUploadBanner">
						<i class="iconfont icon-icon_shengchenghaibao"></i>
						<div>替换背景</div>
					</div>
				</div>
				<TransitionBanner :src="realRenderBanner" :visible="bannerRendered" />
			</template>
			<div v-else class="header-banner-upload">
				<el-image class="header-banner-upload-bg" :src="LAYOUT_UPLOAD_BG_IMG" fit="cover"></el-image>
				<div class="por" style="text-align: center">
					<div class="banner-upload-title">背景图</div>
					<div class="mt-8 banner-upload-desc">6:1，支持jpg、jpeg、png格式，大小不超过10M。</div>
					<el-button type="primary" size="medium" class="mt-16" @click="handleUploadBanner">上传</el-button>
				</div>
			</div>
		</template>
		<TransitionBanner v-else :src="realRenderBanner" :visible="bannerRendered" />
		<div
			class="header-quote-wrap"
			:style="{
				...getMarketColorVars,
				...getTrendColorVars
			}"
		>
			<div class="header-quote">
				<div class="quote-price-box">
					<div class="logo-img"><img :src="logo" alt="" /></div>
					<div>
						<div class="organization-info">
							<p class="organization">{{ getOrganizationName }}</p>
							<div class="stock-code-box">
								<span class="stock-mk">{{ getBaseData.market.toLocaleUpperCase() }}</span>
								<span class="stock-code">{{ getBaseData.code }}</span>
							</div>
						</div>
						<div class="price-info">
							<p class="price" :class="[stockQuoteData.change > 0 ? 'price--up' : 'price--down']">
								<span class="close-price">{{ stockQuoteData.closePrice }} </span
								><span class="symbol">{{ symbol }}</span
								><span class="price-change"
									>{{ stockQuoteData.change > 0 ? '+' : ''
									}}{{ stockQuoteData.change?.toFixed(2) }}</span
								><span class="price-change">{{ stockQuoteData.changePct?.toFixed(2) + '%' }}</span>
							</p>
						</div>
					</div>
				</div>
				<div class="quote-main-data">
					<div class="quote-main-data-item" v-for="item in mainData" :key="item.label">
						<div class="quote-main-data-value">{{ item.value }}{{ item.unit }}</div>
						<div class="quote-main-data-label">{{ item.label }}</div>
					</div>
				</div>
			</div>
			<div class="quote-date">{{ formatDateFn(stockQuoteData.tradingDay) }}</div>
		</div>
	</div>
</template>

<script setup>
import { computed, defineExpose, ref, getCurrentInstance, watch } from 'vue';
import TransitionBanner from '@/views/layout/components/TransitionBanner.vue';
import LAYOUT_UPLOAD_BG_IMG from '@/assets/images/layout_upload_bg.png';
import { useCurrentReactive } from './context';
import { useMarketColors } from '@rebuild/materials/shared/quote/useMarketColors';
import { useCommonData } from '@/hooks';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { usePolling } from '@rebuild/materials/shared/quote/usePolling';
import { useStore } from '@/utils/vue-api';
import { convertNumberToCNUnit } from '@/utils/api';
import { EN } from '@/locales';
import dayjs from 'dayjs';

const { isEnableUpload, realRenderBanner, bannerRendered, create, handleUploadBanner } = useCurrentReactive();

const { getMarketColorVars, getTrendColorVars } = useMarketColors();

const { getOrganizationName, getColumnData } = useCommonData();
const { logo } = getColumnData.value;

const { quote } = useModuleData();
const stockQuoteData = computed(() => quote.data?.data || {});

const store = useStore();
const getBaseData = computed(() => store.getters.getBaseData);
const symbol = computed(() => store.getters.getCurrUnitTips);

const onceLoaded = ref(false);
const fetchStockQuote = async () => {
	await quote.refresh({ realType: 1 });

	if (!onceLoaded.value) {
		onceLoaded.value = true;
	}
};
usePolling(fetchStockQuote);

const { proxy } = getCurrentInstance();
const rowArray = [
	{
		label: proxy.$t(`enterprise.market-cap-in-hk09606`),
		prop: 'totalMv'
	},
	{
		label: proxy.$t(`enterprise.turnover`),
		prop: 'turnoverValue'
	},
	{
		label: proxy.$t(`enterprise.volume`),
		prop: 'turnoverVolumeShares'
	}
];

const formatValueFn = (propName, value) => {
	if (propName == 'changePct') {
		return `${value}%`;
	}
	return value;
};
const formatUnitFn = (propName, unit) => {
	if (propName === 'turnoverVolume') {
		return proxy.$i18n.locale === EN ? `${unit}` : `${unit}手`;
	}
	if (propName == 'turnoverVolumeShares') {
		return proxy.$i18n.locale === EN ? `${unit}` : `${unit}股`;
	}
	return unit;
};

const mainData = ref([]);

const initMainData = data => {
	const dataCopy = JSON.parse(JSON.stringify(data));
	mainData.value = rowArray.map(childItem => {
		let displayValue = dataCopy[childItem.prop];
		const { value, unit } = convertNumberToCNUnit(displayValue);
		return {
			label: childItem.label,
			value: childItem.empty ? '' : formatValueFn(childItem.prop, value),
			unit: formatUnitFn(childItem.prop, unit)
		};
	});
};

// 监听股票数据变化
watch(
	() => stockQuoteData.value,
	newData => {
		if (newData && Object.keys(newData).length) {
			initMainData(newData);
			console.log(mainData.value);
		}
	},
	{ immediate: true }
);

const formatDateFn = date => {
	const formatTime = dayjs(date).valueOf();
	const formatValue = dayjs(formatTime).format('YYYY-MM-DD');

	return `${proxy.$t('enterprise.common-a.until')} ${formatValue} 16:00  （${proxy.$t('enterprise.UTC')}）`;
};


defineExpose({
	create
});
</script>

<style lang="less" scoped>
.header-banner {
	height: 520px;
	position: relative;
	background: #f7f8fa;
	display: flex;
	flex-direction: column;

	.header-banner-mask {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.6);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 2;
		opacity: 0;
		transition: all 0.17s ease-in-out;
		cursor: pointer;

		.header-banner-change {
			padding: 0 16px;
			height: 32px;
			border-radius: 2px;
			background: #fff;
			color: #3964e5;
			display: inline-flex;
			align-items: center;

			.iconfont {
				font-size: 16px;
			}
			div {
				margin-left: 4px;

				font-size: 14px;

				font-weight: 400;
				line-height: 18px; /* 128.571% */
			}
		}
	}
	&:hover {
		.header-banner-mask {
			opacity: 1;
		}
	}
	.header-banner-upload {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		background: #f7f8fa;
		position: relative;

		&-bg {
			position: absolute;
			top: 64px;
			left: 0;
			right: 0;
			bottom: 0;
			margin: auto;
			max-width: 1380px;
		}
	}
	.banner-upload-title {
		color: #1d2129;

		font-size: 16px;

		font-weight: 600;
		line-height: 24px; /* 150% */
	}
	.banner-upload-desc {
		color: #86909c;

		font-size: 12px;

		font-weight: 400;
		line-height: 16px; /* 133.333% */
	}
}

.header-quote-wrap {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: rgba(255, 255, 255, 0.8);
	backdrop-filter: blur(50px);
}

.header-quote {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 0 auto;
	width: var(--pc-layout-min-width);
	height: 120px;
	flex-shrink: 1;
	.quote-price-box {
		display: flex;
		align-items: flex-start;
		padding: 20px 0;
		height: 100%;
		gap: 12px;
		.logo-img {
			width: 40px;
			height: 40px;
			border-radius: 50%;
			border: 1px solid #f2f3f5;
			overflow: hidden;
			img {
				width: 100%;
				height: 100%;
			}
		}
		.organization-info {
			display: flex;
			.organization {
				padding-right: 12px;
				color: var(--t-emphasis-color, #1d2129);
				font-size: 24px;
				font-weight: 500;
				line-height: 32px;
			}
			.stock-code-box {
				margin-top: 4px;
				display: flex;
				align-items: center;
				.stock-mk {
					padding: 2px 8px;
					font-size: 10px;
					font-weight: 600;
					line-height: 14px; /* 140% */

					color: #f23d3d;
					background: var(
						---,
						linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%),
						#f23d3d
					);
				}
				.stock-code {
					padding: 2px 8px;
					color: #fff;
					font-size: 10px;
					font-weight: 600;
					line-height: 14px; /* 140% */
					background: #000;
				}
			}
		}
		.price-info {
			.close-price {
				color: var(--t-emphasis-color, #1d2129);
				font-family: D-DIN;
				font-size: 32px;
				font-weight: 700;
				line-height: 40px; /* 125% */
			}
			.symbol {
				padding-right: 9px;
				color: var(--t-emphasis-color, #1d2129);
				font-size: 14px;
				font-weight: 500;
				line-height: 18px;
			}
			.price-change {
				font-family: D-DIN;
				font-size: 16px;
				font-weight: 700;
				line-height: 24px; /* 150% */
				& + .price-change {
					padding-left: 24px;
				}
			}
			.price--up .price-change {
				color: var(--market-trend-color--up);
			}
			.price--down .price-change {
				color: var(--market-trend-color--down);
			}
		}
	}

	.quote-main-data {
		display: flex;
		gap: 80px;
		.quote-main-data-item {
			.quote-main-data-value {
				color: var(--t-emphasis-color, #1d2129);
				font-family: D-DIN;
				font-size: 24px;
				font-weight: 700;
				line-height: 32px; /* 133.333% */
			}
			.quote-main-data-label {
				color: var(--t-secondary-info-color, #86909c);
				font-size: 14px;
				font-weight: 400;
				line-height: 18px; /* 128.571% */
			}
		}
	}
}
.quote-date {
	position: absolute;
	bottom: 20px;
	right: 20px;
	color: var(--t-secondary-info-color, #86909c);
	font-size: 10px;
	font-weight: 400;
	line-height: 14px; /* 140% */
}
</style>
